import PropTypes from "prop-types";
import Box from "@mui/material/Box";
//import makeStyles from "@material-ui/styles";

import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";

import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
  DataGrid,
} from "@mui/x-data-grid";

import { useState, useEffect } from "react";
import { Delete, Get, Post, Put } from "../../Common/WebRequest";
import DeleteConfirmation from "../../components/DeleteConfirmation";
import { slice } from "../../Common/Utility";
import columnBuilder from "./columnBuilder";
import EditToolbar from "./EditToolbar";

export default function CoursesGrid() {
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [coursesData, setCoursesData] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Delete modal state
  const [type, setType] = useState(null);
  const [id, setId] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] =
    useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);

  const clearMessages = () => {
    setError("");
    setSuccessMsg("");
  };

  const handleError = (error, operation) => {
    console.error(`Error during ${operation}:`, error);
    const errorMessage = error.Status
      ? `${error.Status}/${error.Error}/${error.ErrorDetail}`
      : error.message || "Ein unerwarteter Fehler ist aufgetreten";
    setError(errorMessage);
    setSuccessMsg("");
  };

  const loadData = async () => {
    setIsLoading(true);
    clearMessages();

    try {
      const coursesRes = await Get("course");
      if (!coursesRes.isSuccess) throw coursesRes;
      setCoursesData(coursesRes.result);
    } catch (err) {
      handleError(err, "data loading");
      setCoursesData([]); // Reset to empty on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [reload]);

  const showDeleteModal = (type, id) => {
    try {
      const courseToDelete = coursesData.find((x) => x.id === id);
      if (!courseToDelete) throw new Error("Fach nicht gefunden");

      setType(type);
      setId(id);
      setDeleteMessage(
        `Möchten Sie „${courseToDelete.courseName}" wirklich löschen?`
      );
      setDisplayConfirmationModal(true);
    } catch (err) {
      handleError(err, "delete modal");
    }
  };

  const executeDelete = async () => {
    clearMessages();

    try {
      const deleteResponse = await Delete("course", id);
      if (!deleteResponse.isSuccess) throw deleteResponse;

      setSuccessMsg("Das Fach wurde erfolgreich gelöscht.");
      setReload(!reload);
    } catch (err) {
      handleError(err, "deletion");
      await loadData(); // Reload data to ensure consistency
    }
  };

  const handleDeleteClick = (id) => () => showDeleteModal("", id);

  const handleAfterDeleteClick = () => {
    setDisplayConfirmationModal(false);
    executeDelete();
  };

  async function handleSaveOperation(newRow, updatedRow) {
    clearMessages();
    const originalData = [...coursesData];

    try {
      setCoursesData(
        coursesData.map((row) => (row.id === newRow.id ? updatedRow : row))
      );

      const saveResponse =
        updatedRow.id === 0
          ? await Post("course", { ...updatedRow, isNew: true })
          : await Put("course", updatedRow.id, newRow);

      if (!saveResponse.isSuccess) {
        throw saveResponse;
      }

      setSuccessMsg(
        updatedRow.id === 0
          ? "Das neue Fach wurde hinzugefügt."
          : "Das Fach wurde aktualisiert."
      );

      setTimeout(() => {
        setReload(!reload);
      }, 1000);
    } catch (err) {
      handleError(err, "saving");
      setCoursesData(originalData);
      loadData();
      throw err;
    }
  }

  const handleEditClick = (id) => () => {
    clearMessages();
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "courseName" },
    });
  };

  const handleCancelClick = (id) => () => {
    try {
      setRowModesModel({
        ...rowModesModel,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      });

      const editedRow = coursesData.find((row) => row.id === id);
      if (editedRow?.isNew) {
        setCoursesData(coursesData.filter((row) => row.id !== id));
      }
      clearMessages();
    } catch (err) {
      handleError(err, "canceling edit");
      loadData();
    }
  };

  const handleSaveClick = (id) => async (event) => {
    event.stopPropagation();
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  async function processRowUpdate(newRow) {
    try {
      const updatedRow = { ...newRow, isNew: false };
      await handleSaveOperation(newRow, updatedRow);
      return updatedRow;
    } catch (err) {
      handleError(err, "row update");
      throw err;
    }
  }

  const columns = columnBuilder({
    rowModesModel,
    handleEditClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Box
      sx={{
        height: 800,
        width: "95%",
        "& .actions": { color: "text.secondary" },
        "& .textPrimary": { color: "text.primary" },
      }}
    >
      <br />
      <h2>Nachhilfefächer</h2>
      <br />
      {error && (
        <div className="alert alert-danger" role="alert">
          {slice(error, 200, "...")}
        </div>
      )}

      {!error && successMsg && (
        <div className="alert alert-success" role="alert">
          {slice(successMsg, 100, "...")}
        </div>
      )}
      <br />
      <DataGrid
        rowSelection={false}
        density="compact"
        hideFooterPagination
        rows={coursesData}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStart={(params, event) => (event.defaultMuiPrevented = true)}
        onRowEditStop={(params, event) => (event.defaultMuiPrevented = true)}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(err) => handleError(err, "grid update")}
        slots={{
          toolbar: EditToolbar,
        }}
        slotProps={{
          toolbar: { setCoursesData, setRowModesModel },
        }}
      />
      <DeleteConfirmation
        showModal={displayConfirmationModal}
        confirmModal={handleAfterDeleteClick}
        hideModal={() => setDisplayConfirmationModal(false)}
        type={type}
        id={id}
        message={deleteMessage}
      />
    </Box>
  );
}
